<template>
	<div class="rb-player"
		 :class="{
		 	fullScreen: fullScreen,
		 	uiVisible: uiVisible,
		 	paused: !video.playing,
		 	loading: video.loading
		 }"
		 @click="onMouseClick"
		 @dblclick="onMouseDoubleClick"
		 @mousemove="onMouseMove"
		 @keydown="onKeyDown"
		 :tabindex="-1"
	>
		<transition name="fade">
			<video ref="video" v-show="video.loaded"></video>
		</transition>

		<div class="overlay">
			<transition name="fade-slow">
				<div class="rb-poster" :class="{casting: video.casting}" v-if="poster && ((!video.position && !video.playing) || video.casting)">
					<img :src="poster">
					<rb-icon brand v-if="video.casting">chromecast</rb-icon>
				</div>
			</transition>

			<transition name="fade-slow">
				<rb-watch-party
					v-if="user"
					:user="user"
					:video="video"

					v-show="uiVisible"
				/>
			</transition>

			<transition name="fade-slow">
				<rb-player-bar
					:sources="sources"
					:cues="cues.filter(x => x)"
					:video="video"

					class="ui bottom"
					v-show="uiVisible"
					v-if="video.loaded"

					@fullscreen="toggleFullScreen"
					@cast="toggleCast"
					@mouseenter.native="onMouseEnterUi"
					@mouseleave.native="onMouseLeaveUi"
				></rb-player-bar>
			</transition>

			<transition name="fade-slow">
				<rb-next-video-ribbon v-if="showNextVideoRibbon" @end="$emit('next')" />
			</transition>

			<rb-loader center v-if="video.loading"></rb-loader>

			<slot></slot>
		</div>
	</div>
</template>

<script>
	import RbLoader from '../RbLoader';
	import RbPlayerBar from './components/RbPlayerBar';
	import {Keys} from '../../../../shared/util/Keys';
	import {VideoCue, VideoPlayer} from './VideoPlayer';
	import {VideoSettings} from './VideoSettings';
	import RbNextVideoRibbon from './components/RbNextVideoRibbon';
	import RbWatchParty from './components/RbWatchParty/RbWatchParty';

	export default {
		name: 'RbVideoPlayer',
		components: { RbWatchParty, RbNextVideoRibbon, RbPlayerBar, RbLoader},
		props: {
			/** @type {User} **/
			user: {
				default: null
			},
			sources: {
				type: Array,
				required: true
			},
			metaData: {
				type: Object,
				default: () => {}
			},
			poster: {
				type: String,
				default: ''
			},
			cues: {
				type: Array,
				default: () => []
			},
			autoFocus: {
				type: Boolean,
				default: true
			},
			autoPlay: {
				type: Boolean,
				default: false
			},
			openingEnd: {
				type: VideoCue,
				default: null
			},
			endingStart: {
				type: VideoCue,
				default: null
			},
			hasNextVideo: {
				type: Boolean,
				default: false
			}
		},
		data: () => ({
			video: new VideoPlayer(),

			showUi: false,
			mouseOverUi: false,
			hideUiTime: 1500,
			hideUiTimeout: null,
			fullScreen: false,
			preFullScreenOrientation: null,
			showNextVideoRibbon: false
		}),
		watch: {
			sources() {
				this.resetVideo();
			},
			metaData() {
				this.updateMediaSessionMetadata();
			}
		},
		methods: {
			resetVideo() {
				this.video.reset();
				this.showNextVideoRibbon = false;

				this.chooseInitialSource();

				if (VideoSettings.skipOpening && this.openingEnd) {
					this.video.seek(this.openingEnd.position);
				}

				if (this.autoPlay) {
					this.video.play();
				}
			},

			chooseInitialSource() {
				if (this.sources.length) {
					let prefferedSize = VideoSettings.size;

					let source = this.sources.find(s => s.quality.size === prefferedSize);
					if (!source) {
						source = this.sources.filter(s => s.quality.size <= prefferedSize).last();
					}
					if (!source) {
						source = this.sources[0];
					}

					this.video.setSource(source);
				} else {
					this.video.setSource(null);
				}
			},

			/// ACTIONS
			// Ui state
			hideUiSoon() {
				this.cancelHideUiSoon();

				this.hideUiTimeout = window.setTimeout(() => {
					this.showUi = false;
				}, this.hideUiTime);
			},

			cancelHideUiSoon() {
				if (this.hideUiTimeout) {
					window.clearTimeout(this.hideUiTimeout);
				}
			},

			// Full screen
			toggleFullScreen() {
				if (!document.fullscreenElement) {
					this.$el.requestFullscreen();
				} else {
					if (document.exitFullscreen) {
						document.exitFullscreen();
					}
				}
			},
			onFullScreenChange() {
				this.fullScreen = document.fullscreenElement === this.$el;

				if (this.fullScreen) {
					this.preFullScreenOrientation = screen.orientation;

					screen.orientation.lock("landscape")
						.catch(function(error) {
							console.log(error);
						});
				} else {
					screen.orientation.unlock();
				}
			},

			// Cast
			toggleCast() {
				this.video.toggleCast();
			},

			/// EVENTS
			// Global
			bindGlobalEvents() {
				document.addEventListener('fullscreenchange', this.onFullScreenChange);
			},
			unbindGlobalEvents() {
				document.removeEventListener('fullscreenchange', this.onFullScreenChange);
			},

			// Player
			onUpdatePosition() {
				this.updateMediaSessionPlaybackState();

				if (this.endingStart && this.video.position >= this.endingStart.position) {
					if (VideoSettings.skipEnding) this.video.stop();
				}
			},
			onVideoEnd() {
				this.updateMediaSessionPlaybackState();

				if (this.hasNextVideo && VideoSettings.autoPlay) {
					this.showNextVideoRibbon = true;
				} else if (this.fullScreen) {
					this.toggleFullScreen();
				}
			},
			onVideoPlay() {
				this.updateMediaSessionPlaybackState();
				this.showNextVideoRibbon = false;
			},

			// Key
			onKeyDown(e) {
				const prevent = () => { e.stopPropagation(); e.preventDefault(); };

				if (e.ctrlKey || e.altKey) return;

				switch (e.which) {
					case Keys.Space:
						this.video.togglePlay();

						prevent();
						break;

					case Keys.F:
						this.toggleFullScreen();

						prevent();
						break;

					case Keys.ArrowLeft:
						this.video.skip(-1);

						prevent();
						break;

					case Keys.ArrowRight:
						this.video.skip(1);

						prevent();
						break;

					case Keys.ArrowUp:
						if (this.video.volume < 1){
							this.video.setVolume(this.video.volume + .05);
						}

						prevent();
						break;

					case Keys.ArrowDown:
						if (this.video.volume > 0){
							this.video.setVolume(this.video.volume - .05);
						}

						prevent();
						break;

					case Keys.M:
						this.video.toggleMute();

						prevent();
						break;

					case Keys.Home:
						this.video.seek(0);

						prevent();
						break;
				}
			},

			// Mouse interaction
			onMouseClick() {
				if (this.video.canPlay) this.video.togglePlay();
			},
			onMouseDoubleClick() {
				this.toggleFullScreen();
			},
			onMouseMove() {
				if (this.mouseOverUi) return;

				this.showUi = true;
				this.hideUiSoon();
			},

			onMouseEnterUi() {
				this.mouseOverUi = true;
				this.cancelHideUiSoon();
			},
			onMouseLeaveUi() {
				this.mouseOverUi = false;
				this.hideUiSoon();
			},


			// Media session
			updateMediaSessionMetadata() {
				if ('mediaSession' in navigator && this.metaData.title) {
					navigator.mediaSession.metadata = new window.MediaMetadata({
						title: this.metaData.title,
						artist: this.metaData.artist,
						album: this.metaData.album,
						artwork: [
							{ src: this.poster, sizes: '512x512', type: 'image/jpg' },
						]
					});

					this.updateMediaSessionPlaybackState();

					navigator.mediaSession.setActionHandler('play', () => this.video.play() );
					navigator.mediaSession.setActionHandler('pause', () => this.video.pause() );
					navigator.mediaSession.setActionHandler('seekbackward', () => this.video.skip(-3) );
					navigator.mediaSession.setActionHandler('seekforward', () => this.video.skip(3) );
					navigator.mediaSession.setActionHandler('previoustrack', () => this.video.seek(0) );
					navigator.mediaSession.setActionHandler('nexttrack', () => this.video.seek(this.video.duration) );
				}
			},
			updateMediaSessionPlaybackState() {
				if ('mediaSession' in navigator) {
					navigator.mediaSession.playbackState = this.video.playing ? 'playing' : 'paused';
				}
			},
			clearMediaSession() {
				if ('mediaSession' in navigator) {
					navigator.mediaSession.metadata = null;

					navigator.mediaSession.setActionHandler('play', () => this.play() );
					navigator.mediaSession.setActionHandler('pause', null );
					navigator.mediaSession.setActionHandler('seekbackward', null );
					navigator.mediaSession.setActionHandler('seekforward', null );
					navigator.mediaSession.setActionHandler('previoustrack', null );
					navigator.mediaSession.setActionHandler('nexttrack', null );

					navigator.mediaSession.playbackState ='none';
				}
			}
		},
		computed: {
			uiVisible() {
				return this.showUi || this.video.casting || this.video.loading || !this.video.playing;
			}
		},
		mounted() {
			this.video.setVideoElement(this.$refs.video);

			if (this.sources.length) {
				this.resetVideo()
			}

			this.bindGlobalEvents();

			if (this.autoFocus) this.$el.focus();


			/// Settings
			this.video.setVolume(VideoSettings.volume);
			this.video.on('update:volume', () => VideoSettings.volume = this.video.volume);

			if (VideoSettings.muted) this.video.mute();
			this.video.on('update:muted', () => VideoSettings.muted = this.video.muted);

			this.video.on('sourceSwap', () => VideoSettings.size = this.video.currentSource.quality.size);

			this.video.onAny((event, ...data) => {
				this.$emit(event, ...data);
			});

			/// MediaSession
			this.updateMediaSessionMetadata();

			this.video.on('update:position', () => this.onUpdatePosition());
			this.video.on('play', () => this.onVideoPlay());
			this.video.on('pause', () => this.updateMediaSessionPlaybackState());
			this.video.on('end', () => this.onVideoEnd());
		},
		beforeDestroy() {
			this.unbindGlobalEvents();
			this.clearMediaSession();
		}
	}
</script>

<style lang="scss" scoped src="./RbVideoPlayer.scss"></style>